import React, { MouseEvent, useMemo, useRef, useState } from 'react';
import { GenericObject } from 'types/generic.types';
import { Box, Tooltip } from '@chakra-ui/react';
import { DataTestId } from 'types/general.types';
import { TruncatableChip } from './TruncatableChip';

interface TruncatedItem {
  label: string;
  width: number;
}

const addItem = (
  items: GenericObject<TruncatedItem>,
  itemKey: string,
  item: TruncatedItem
) => {
  if (itemKey in items) {
    return items;
  }
  return { ...items, [itemKey]: item };
};

const removeItem = (items: GenericObject<TruncatedItem>, itemKey: string) => {
  if (itemKey in items) {
    const { [itemKey]: _, ...restItems } = items;
    return restItems;
  }
  return items;
};

const updateItemsRef = (
  itemsRef: React.MutableRefObject<{}>,
  key: string,
  chipLabel: string,
  entry: IntersectionObserverEntry,
  add: boolean
) => {
  // eslint-disable-next-line no-param-reassign
  itemsRef.current = add
    ? addItem(itemsRef.current, key, {
        label: chipLabel,
        width: entry?.boundingClientRect.width,
      })
    : removeItem(itemsRef.current, key);

  return itemsRef.current;
};

interface TruncatedChipsProps<T> {
  getLabel: (value: T) => string;
  getKey: (value: T) => string;
  items: T[];
  onClick?: (value: T, e: MouseEvent) => void;
}

const SPACING_BETWEEN_ITEMS = 8;

export const TruncatedChips = <T extends {}>({
  getLabel,
  getKey,
  items,
  onClick,
  'data-testid': dataTestId,
}: TruncatedChipsProps<T> & DataTestId) => {
  const containerRef = useRef(null);
  const visibleItemsRef = useRef({});
  const hiddenItemsRef = useRef({});
  const [hiddenItems, setHiddenItems] = useState<GenericObject<TruncatedItem>>(
    {}
  );
  const [visibleItems, setVisibleItems] = useState<
    GenericObject<TruncatedItem>
  >({});

  const offset = useMemo(
    () =>
      Object.values(visibleItems).reduce(
        (acc, { width }) => acc + width + SPACING_BETWEEN_ITEMS,
        0
      ),
    [visibleItems]
  );

  const hiddenItemLabels = useMemo(
    () => Object.values(hiddenItems).map((item) => item.label),
    [hiddenItems]
  );

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          overflow: 'hidden',
          maxWidth: items.length === 1 ? '100%' : 'calc(100% - 60px)',
        }}
      >
        {items.map((item) => {
          const chipLabel = getLabel(item);
          const key = getKey(item);
          return (
            <TruncatableChip
              dataTestId={dataTestId}
              key={key}
              label={chipLabel}
              containerRef={containerRef}
              onClick={(e) => {
                if (onClick) {
                  onClick(item, e);
                }
              }}
              onChange={(inView, entry) => {
                setVisibleItems(
                  updateItemsRef(visibleItemsRef, key, chipLabel, entry, inView)
                );
                setHiddenItems(
                  updateItemsRef(hiddenItemsRef, key, chipLabel, entry, !inView)
                );
              }}
              tagProps={{
                bg: 'M.10',
              }}
            />
          );
        })}
      </Box>
      {hiddenItemLabels.length > 0 && (
        <Tooltip label={hiddenItemLabels.join(', ')} hasArrow cursor="pointer">
          <Box
            sx={{
              position: 'absolute',
              left: offset,
              bottom: '8px',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: '12px',
            }}
          >
            {`+ ${hiddenItemLabels.length} ${
              hiddenItemLabels.length === 1 ? 'role' : 'roles'
            }`.toUpperCase()}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
